export type ImportedSymbolGeometry = {
  semanticType: string;
  libraryKey?: string | null;
  width: number;
  height: number;
  detectedWidth?: number | null;
  detectedHeight?: number | null;
  rotation?: number | null;
};

export type ImportedRectangleFallback = {
  kind: "counter" | "cabinet" | "wardrobe";
  label: string;
  height: number;
  baseElevation: number;
};

const MIN_ORIENTATION_ASPECT_RATIO = 1.15;

const positiveDimension = (value: number | null | undefined) =>
  typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : undefined;

export const getImportedSymbolFootprint = (symbol: ImportedSymbolGeometry) => ({
  width:
    positiveDimension(symbol.detectedWidth) ??
    positiveDimension(symbol.width) ??
    0.8,
  height:
    positiveDimension(symbol.detectedHeight) ??
    positiveDimension(symbol.height) ??
    0.8,
});

const majorAxisOffset = (width: number, height: number) => {
  const shortSide = Math.min(width, height);
  const longSide = Math.max(width, height);
  if (!(shortSide > 0) || longSide / shortSide < MIN_ORIENTATION_ASPECT_RATIO) {
    return undefined;
  }
  return height > width ? Math.PI / 2 : 0;
};

const normalizeRadians = (value: number) => {
  const fullTurn = Math.PI * 2;
  return ((((value + Math.PI) % fullTurn) + fullTurn) % fullTurn) - Math.PI;
};

/**
 * Detector bounds describe the detected object's local axes. Catalog artwork
 * has its own base orientation, so portrait detections need a quarter turn
 * when the selected catalog asset is landscape (and vice versa).
 */
export const resolveImportedCatalogRotation = ({
  symbol,
  assetWidth,
  assetHeight,
}: {
  symbol: ImportedSymbolGeometry;
  assetWidth: number;
  assetHeight: number;
}) => {
  const rotation = Number.isFinite(symbol.rotation) ? symbol.rotation! : 0;
  const footprint = getImportedSymbolFootprint(symbol);
  const detectedOffset = majorAxisOffset(footprint.width, footprint.height);
  const assetOffset = majorAxisOffset(assetWidth, assetHeight);
  if (detectedOffset === undefined || assetOffset === undefined) {
    return rotation;
  }
  return normalizeRadians(rotation + detectedOffset - assetOffset);
};

export const getImportedRectangleFallback = (
  semanticType: string,
): ImportedRectangleFallback | undefined => {
  const value = semanticType.toLowerCase().replace(/[_-]+/g, " ").trim();

  if (
    /\b(wardrobe|closet|garderob|garderobe|coat cupboard|linen cupboard|full height cabinet|tall cabinet)\b/.test(
      value,
    )
  ) {
    return {
      kind: "wardrobe",
      label: "Wardrobes",
      height: 2.1,
      baseElevation: 0,
    };
  }
  if (
    /\b(counter|countertop|worktop|work surface|benchtop|breakfast bar)\b/.test(
      value,
    )
  ) {
    return {
      kind: "counter",
      label: "Counters",
      height: 0.5,
      baseElevation: 0,
    };
  }
  if (/\b(wall cabinet|upper cabinet|overhead cabinet)\b/.test(value)) {
    return {
      kind: "cabinet",
      label: "Cabinets",
      height: 0.7,
      baseElevation: 1.4,
    };
  }
  if (
    /\b(cabinet|cupboard|base unit|wall unit|kitchen unit|storage unit|skåp)\b/.test(
      value,
    )
  ) {
    return {
      kind: "cabinet",
      label: "Cabinets",
      height: 0.9,
      baseElevation: 0,
    };
  }
  return undefined;
};

export const isFloorplanImportSymbolInsertable = (
  symbol: ImportedSymbolGeometry,
) =>
  Boolean(
    symbol.libraryKey || getImportedRectangleFallback(symbol.semanticType),
  );
